// Mock data for Motivo Basic demo pages
// This data is used for "smoke and mirrors" demos - no actual backend integration

// Avatar images for Motivo supervisors
import aliciaMAvatar from '../../assets/images/basic/alicia-m.png';
import gustaveRAvatar from '../../assets/images/basic/gustave-r.png';

// Pravatar URLs for placeholder avatars (using unique seeds for each person)
const pravatar = (seed: string) => `https://i.pravatar.cc/150?u=${seed}`;

export type AlertType = 'details_out_of_date';

export interface MockSupervisorProfile {
  phone: string;
  address: {
    street: string;
    unit?: string;
    city: string;
    state: string;
    zip: string;
  };
  timezone: string;
  emergencyContact: {
    name: string;
    phone: string;
    email: string;
  };
  licenses: {
    type: string;
    state: string;
    number: string;
    expirationDate: string;
    isExpired?: boolean;
    canSuperviseFor?: boolean;
  }[];
  supervisionCredentials: {
    name: string;
    issuingOrg: string;
    number: string;
    state: string;
    expirationDate: string;
  }[];
  telehealthDocuments: string[];
  practicingSince?: string;
  supervisingSince?: string;
  resumeFile?: string;
  education: {
    degree: string;
    institution: string;
    year: string;
  }[];
  experience: {
    title: string;
    organization: string;
    years: string;
  }[];
}

export interface MockSupervisor {
  id: string;
  firstName: string;
  lastName: string;
  prefix?: string;
  suffix?: string;
  email: string;
  avatarUrl?: string;
  bio?: string;
  isVetted?: boolean;
  isMotivoSupervisor?: boolean;
  alert?: AlertType;
  profile?: MockSupervisorProfile;
}

export interface SuperviseeComplianceItem {
  id: string;
  name: string;
  description: string;
  status: 'complete' | 'in_progress' | 'not_started' | 'overdue';
  dueDate?: string;
  completedDate?: string;
}

export interface MockSuperviseeProfile {
  phone: string;
  address: {
    street: string;
    unit?: string;
    city: string;
    state: string;
    zip: string;
  };
  timezone: string;
  emergencyContact: {
    name: string;
    phone: string;
    email: string;
  };
  superviseeType: string;
  employmentStatus: string;
  workModality: string;
  startDate: string;
  stateRequirements: {
    totalHoursRequired: number;
    individualHoursRequired: number;
    groupHoursRequired: number;
    clientContactHoursRequired: number;
    directSupervisionHoursRequired: number;
  };
  complianceItems: SuperviseeComplianceItem[];
}

export interface MockSupervisee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  licenseType: string;
  hoursSupervised?: number;
  hoursRequired?: number;
  profile?: MockSuperviseeProfile;
}

export const MOCK_SUPERVISORS: MockSupervisor[] = [
  {
    id: '1',

    firstName: 'Jennifer',
    lastName: 'Gengar',
    suffix: 'LCSW',
    email: 'j.gengar@mindwell.org',
    avatarUrl: pravatar('j.gengar@mindwell.org'),
    bio: 'I am a Licensed Clinical Social Worker with over 10 years of experience in mental health counseling and clinical supervision. My approach integrates cognitive-behavioral therapy with mindfulness practices to help clients develop sustainable coping strategies. I specialize in anxiety, depression, and trauma-informed care, and I am passionate about mentoring the next generation of mental health professionals.',
    alert: 'details_out_of_date',
    profile: {
      phone: '212-747-8273',
      address: {
        street: '800 Lavendar Street',
        unit: 'Apartment 2',
        city: 'New York',
        state: 'NY',
        zip: '10938',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Ash Ketchum',
        phone: '212-746-3917',
        email: 'catchemall@gmail.com',
      },
      licenses: [
        {
          type: 'LCSW',
          state: 'New York',
          number: '1233457',
          expirationDate: 'Nov 2024',
          isExpired: true,
          canSuperviseFor: true,
        },
      ],
      supervisionCredentials: [
        {
          name: 'Certified Clinical Supervisor (CCS)',
          issuingOrg: 'Center for Credentialing & Education',
          number: '4846260',
          state: 'New York',
          expirationDate: 'May 2027',
        },
      ],
      telehealthDocuments: ['training_doc.pdf'],
      practicingSince: '2015',
      supervisingSince: '2020',
      resumeFile: 'jennifer_hawthorne_resume.pdf',
      education: [
        {
          degree: 'Master of Social Work',
          institution: 'Columbia University',
          year: '2015',
        },
        {
          degree: 'Bachelor of Arts, Psychology',
          institution: 'NYU',
          year: '2012',
        },
      ],
      experience: [
        {
          title: 'Clinical Supervisor',
          organization: 'MindWell Therapy',
          years: '2020 - Present',
        },
        {
          title: 'Licensed Clinical Social Worker',
          organization: 'Brooklyn Mental Health Center',
          years: '2016 - 2020',
        },
      ],
    },
  },
  {
    id: '2',

    firstName: 'Maisie',
    lastName: 'Hampshere',
    suffix: 'LCSW',
    email: 'm.hampshere@mindwell.org',
    avatarUrl: pravatar('m.hampshere@mindwell.org'),
    bio: 'With 15 years of experience in community mental health, I focus on helping new clinicians navigate the complexities of working with diverse populations. My supervision approach combines practical skill-building with reflective practice, ensuring supervisees develop both clinical competence and professional resilience. I am particularly passionate about trauma-informed care and social justice in mental health.',
    isVetted: true,
    profile: {
      phone: '212-345-9752',
      address: {
        street: '1435 Beacon Street',
        unit: 'Unit 7',
        city: 'New York',
        state: 'NY',
        zip: '11102',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Peter Macrina',
        phone: '212-347-2383',
        email: 'petermacrina1989@gmail.com',
      },
      licenses: [
        {
          type: 'LCSW',
          state: 'New York',
          number: '2145832',
          expirationDate: 'Jan 2027',
          canSuperviseFor: true,
        },
      ],
      supervisionCredentials: [
        {
          name: 'Board Certified Supervisor (BCS)',
          issuingOrg: 'NASW',
          number: '5923741',
          state: 'New York',
          expirationDate: 'Nov 2026',
        },
      ],
      telehealthDocuments: ['telehealth_training.pdf'],
      practicingSince: '2013',
      supervisingSince: '2019',
      resumeFile: 'maisie_kline_resume.pdf',
      education: [
        {
          degree: 'Master of Social Work',
          institution: 'Fordham University',
          year: '2013',
        },
      ],
      experience: [
        {
          title: 'Senior Clinical Social Worker',
          organization: 'MindWell Therapy',
          years: '2019 - Present',
        },
      ],
    },
  },
  {
    id: '3',

    firstName: 'Leon',
    lastName: 'Jang',
    suffix: 'LMFT',
    email: 'l.jang@mindwell.org',
    avatarUrl: pravatar('l.jang@mindwell.org'),
    bio: 'As a Licensed Marriage and Family Therapist, I bring a systemic perspective to clinical supervision. I believe that strong therapeutic relationships are built on understanding family dynamics and cultural contexts. My supervision style emphasizes collaborative learning, where supervisees are encouraged to explore their own therapeutic voice while developing evidence-based skills.',
    isVetted: true,
    profile: {
      phone: '212-555-1234',
      address: {
        street: '123 Park Avenue',
        city: 'New York',
        state: 'NY',
        zip: '10001',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Mina Jang',
        phone: '212-555-5678',
        email: 'mjang@gmail.com',
      },
      licenses: [
        {
          type: 'LMFT',
          state: 'New York',
          number: '3271845',
          expirationDate: 'Aug 2027',
          canSuperviseFor: true,
        },
      ],
      supervisionCredentials: [
        {
          name: 'AAMFT Approved Supervisor',
          issuingOrg: 'AAMFT',
          number: '6738291',
          state: 'New York',
          expirationDate: 'Jun 2026',
        },
      ],
      telehealthDocuments: ['telehealth_cert.pdf'],
      practicingSince: '2014',
      supervisingSince: '2020',
      resumeFile: 'leon_park_resume.pdf',
      education: [
        {
          degree: 'Master of Marriage and Family Therapy',
          institution: 'Syracuse University',
          year: '2014',
        },
      ],
      experience: [
        {
          title: 'Marriage & Family Therapist',
          organization: 'MindWell Therapy',
          years: '2018 - Present',
        },
      ],
    },
  },
  {
    id: '4',

    firstName: 'Bea',
    lastName: 'Ocasio',
    suffix: 'LPC',
    email: 'b.ocasio@mindwell.org',
    avatarUrl: pravatar('b.ocasio@mindwell.org'),
    bio: 'I am a Licensed Professional Counselor specializing in individual therapy and clinical supervision. My approach is grounded in person-centered theory with an emphasis on developing authentic therapeutic relationships. I support supervisees in building confidence while maintaining ethical standards and professional growth.',
    isVetted: true,
    profile: {
      phone: '212-888-9999',
      address: {
        street: '456 Broadway',
        city: 'New York',
        state: 'NY',
        zip: '10012',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Rosa Ocasio',
        phone: '212-888-0000',
        email: 'rocasio@gmail.com',
      },
      licenses: [
        {
          type: 'LPC',
          state: 'New York',
          number: '4582193',
          expirationDate: 'Sep 2026',
          canSuperviseFor: true,
        },
      ],
      supervisionCredentials: [
        {
          name: 'National Certified Counselor (NCC)',
          issuingOrg: 'NBCC',
          number: '7294135',
          state: 'New York',
          expirationDate: 'Dec 2026',
        },
      ],
      telehealthDocuments: ['training_doc.pdf'],
      practicingSince: '2016',
      supervisingSince: '2021',
      resumeFile: 'bea_ocasio_resume.pdf',
      education: [
        {
          degree: 'Master of Science in Counseling',
          institution: 'Iona College',
          year: '2016',
        },
      ],
      experience: [
        {
          title: 'Licensed Professional Counselor',
          organization: 'MindWell Therapy',
          years: '2020 - Present',
        },
      ],
    },
  },
];

// Training modules that appear on the Resources page
const TRAINING_COMPLIANCE_ITEMS: SuperviseeComplianceItem[] = [
  {
    id: 'training-supervisory-relationships',
    name: 'Training: Best practices for supervisory relationships',
    description: 'Complete the 5-minute training module on supervisory relationship best practices',
    status: 'complete',
    completedDate: 'Dec 10, 2025',
  },
  {
    id: 'training-compliant-documentation',
    name: 'Training: Maintaining compliant documentation',
    description: 'Complete the 8-minute training module on documentation compliance',
    status: 'complete',
    completedDate: 'Dec 12, 2025',
  },
  {
    id: 'training-crisis-intervention',
    name: 'Training: Crisis intervention protocols',
    description: 'Complete the 12-minute training module on crisis intervention procedures',
    status: 'in_progress',
    dueDate: 'Feb 1, 2026',
  },
  {
    id: 'training-ethical-boundaries',
    name: 'Training: Ethical boundaries in clinical supervision',
    description: 'Complete the 10-minute training module on ethical boundaries',
    status: 'not_started',
    dueDate: 'Mar 1, 2026',
  },
  {
    id: 'training-hipaa',
    name: 'Training: HIPAA compliance essentials',
    description: 'Complete the 15-minute training module on HIPAA compliance',
    status: 'not_started',
    dueDate: 'Mar 15, 2026',
  },
];

// Common non-training compliance items
const BASE_COMPLIANCE_ITEMS: SuperviseeComplianceItem[] = [
  {
    id: 'background-check',
    name: 'Background Check',
    description: 'Complete FBI fingerprint-based background check through IdentoGO',
    status: 'complete',
    completedDate: 'Dec 15, 2025',
  },
  {
    id: 'supervision-plan',
    name: 'Supervision Plan Documentation',
    description: 'Submit signed supervision plan with designated supervisor(s)',
    status: 'complete',
    completedDate: 'Oct 1, 2025',
  },
  {
    id: 'supervision-log',
    name: 'Supervision Session Log',
    description: 'Document all supervision sessions with dates, duration, and topics covered',
    status: 'in_progress',
    dueDate: 'Ongoing',
  },
];

// Common compliance items for New York LCSW requirements
const NY_LCSW_COMPLIANCE_ITEMS: SuperviseeComplianceItem[] = [
  ...BASE_COMPLIANCE_ITEMS,
  ...TRAINING_COMPLIANCE_ITEMS,
];

// LMFT supervisees are further along on trainings
const NY_LMFT_COMPLIANCE_ITEMS: SuperviseeComplianceItem[] = [
  ...BASE_COMPLIANCE_ITEMS,
  ...TRAINING_COMPLIANCE_ITEMS.map((item) => {
    if (item.id === 'training-crisis-intervention') {
      return { ...item, status: 'complete' as const, completedDate: 'Dec 18, 2025' };
    }
    if (item.id === 'training-ethical-boundaries') {
      return { ...item, status: 'in_progress' as const };
    }
    return item;
  }),
];

// LPC supervisees are just starting trainings
const LPC_COMPLIANCE_ITEMS: SuperviseeComplianceItem[] = [
  ...BASE_COMPLIANCE_ITEMS,
  ...TRAINING_COMPLIANCE_ITEMS.map((item) => {
    if (item.id === 'training-supervisory-relationships') {
      return { ...item, status: 'in_progress' as const, completedDate: undefined };
    }
    if (item.id === 'training-compliant-documentation') {
      return { ...item, status: 'not_started' as const, completedDate: undefined };
    }
    if (item.id === 'training-crisis-intervention') {
      return { ...item, status: 'not_started' as const };
    }
    return item;
  }),
];

export const MOCK_SUPERVISEES: MockSupervisee[] = [
  {
    id: '101',

    firstName: 'Bonnie',
    lastName: 'Raitt',
    email: 'b.raitt@mindwell.org',
    avatarUrl: pravatar('b.raitt@mindwell.org'),
    licenseType: 'LCSW',
    hoursSupervised: 40,
    hoursRequired: 104,
    profile: {
      phone: '212-555-0101',
      address: {
        street: '245 West 14th Street',
        unit: 'Apt 4B',
        city: 'New York',
        state: 'NY',
        zip: '10011',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'James Raitt',
        phone: '212-555-0102',
        email: 'j.raitt@gmail.com',
      },
      superviseeType: 'Associate',
      employmentStatus: 'Full-time',
      workModality: 'Hybrid',
      startDate: 'Mar 15, 2024',
      stateRequirements: {
        totalHoursRequired: 104,
        individualHoursRequired: 52,
        groupHoursRequired: 52,
        clientContactHoursRequired: 3000,
        directSupervisionHoursRequired: 104,
      },
      complianceItems: NY_LCSW_COMPLIANCE_ITEMS,
    },
  },
  {
    id: '102',

    firstName: 'Ezra',
    lastName: 'Furman',
    email: 'e.furman@mindwell.org',
    avatarUrl: pravatar('e.furman@mindwell.org'),
    licenseType: 'LPC',
    hoursSupervised: 75,
    hoursRequired: 80,
    profile: {
      phone: '212-555-0201',
      address: {
        street: '890 Broadway',
        city: 'New York',
        state: 'NY',
        zip: '10003',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Diana Furman',
        phone: '212-555-0202',
        email: 'd.furman@gmail.com',
      },
      superviseeType: 'Associate',
      employmentStatus: 'Full-time',
      workModality: 'In-person',
      startDate: 'Apr 1, 2024',
      stateRequirements: {
        totalHoursRequired: 80,
        individualHoursRequired: 48,
        groupHoursRequired: 32,
        clientContactHoursRequired: 2000,
        directSupervisionHoursRequired: 80,
      },
      complianceItems: LPC_COMPLIANCE_ITEMS,
    },
  },
  {
    id: '103',

    firstName: 'Ethel',
    lastName: 'Cain',
    email: 'e.cain@mindwell.org',
    avatarUrl: pravatar('e.cain@mindwell.org'),
    licenseType: 'LMFT',
    hoursSupervised: 85,
    hoursRequired: 150,
    profile: {
      phone: '212-555-0301',
      address: {
        street: '123 East 45th Street',
        unit: 'Suite 500',
        city: 'New York',
        state: 'NY',
        zip: '10017',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Patricia Cain',
        phone: '212-555-0302',
        email: 'p.cain@gmail.com',
      },
      superviseeType: 'Associate',
      employmentStatus: 'Full-time',
      workModality: 'Hybrid',
      startDate: 'May 1, 2024',
      stateRequirements: {
        totalHoursRequired: 150,
        individualHoursRequired: 75,
        groupHoursRequired: 75,
        clientContactHoursRequired: 1500,
        directSupervisionHoursRequired: 150,
      },
      complianceItems: NY_LMFT_COMPLIANCE_ITEMS,
    },
  },
  {
    id: '104',

    firstName: 'Rachel',
    lastName: 'Chinouriri',
    email: 'r.chinouriri@mindwell.org',
    avatarUrl: pravatar('r.chinouriri@mindwell.org'),
    licenseType: 'LMFT',
    hoursSupervised: 76,
    hoursRequired: 120,
    profile: {
      phone: '212-555-0401',
      address: {
        street: '567 Park Avenue South',
        city: 'New York',
        state: 'NY',
        zip: '10016',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Marcus Chinouriri',
        phone: '212-555-0402',
        email: 'm.chinouriri@gmail.com',
      },
      superviseeType: 'Associate',
      employmentStatus: 'Full-time',
      workModality: 'Hybrid',
      startDate: 'Jun 1, 2024',
      stateRequirements: {
        totalHoursRequired: 120,
        individualHoursRequired: 60,
        groupHoursRequired: 60,
        clientContactHoursRequired: 1500,
        directSupervisionHoursRequired: 120,
      },
      complianceItems: NY_LMFT_COMPLIANCE_ITEMS,
    },
  },
  {
    id: '105',

    firstName: 'Sean',
    lastName: 'Paul',
    email: 's.paul@mindwell.org',
    avatarUrl: pravatar('s.paul@mindwell.org'),
    licenseType: 'LPC',
    hoursSupervised: 33,
    hoursRequired: 200,
    profile: {
      phone: '212-555-0501',
      address: {
        street: '789 Lexington Avenue',
        unit: 'Apt 12C',
        city: 'New York',
        state: 'NY',
        zip: '10065',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Marcus Paul',
        phone: '212-555-0502',
        email: 'm.paul@gmail.com',
      },
      superviseeType: 'Associate',
      employmentStatus: 'Full-time',
      workModality: 'In-person',
      startDate: 'Sep 1, 2025',
      stateRequirements: {
        totalHoursRequired: 200,
        individualHoursRequired: 120,
        groupHoursRequired: 80,
        clientContactHoursRequired: 2000,
        directSupervisionHoursRequired: 200,
      },
      complianceItems: LPC_COMPLIANCE_ITEMS,
    },
  },
  {
    id: '106',
    firstName: 'Arlo',
    lastName: 'Parks',
    email: 'a.parks@mindwell.org',
    avatarUrl: pravatar('a.parks@mindwell.org'),
    licenseType: 'LMFT',
    hoursSupervised: 98,
    hoursRequired: 102,
    profile: {
      phone: '212-555-0601',
      address: {
        street: '321 West 23rd Street',
        city: 'New York',
        state: 'NY',
        zip: '10011',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'James Parks',
        phone: '212-555-0602',
        email: 'j.parks@gmail.com',
      },
      superviseeType: 'Associate',
      employmentStatus: 'Full-time',
      workModality: 'Hybrid',
      startDate: 'Aug 15, 2024',
      stateRequirements: {
        totalHoursRequired: 102,
        individualHoursRequired: 51,
        groupHoursRequired: 51,
        clientContactHoursRequired: 1500,
        directSupervisionHoursRequired: 102,
      },
      complianceItems: NY_LMFT_COMPLIANCE_ITEMS,
    },
  },
];

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function formatSupervisorName(supervisor: MockSupervisor): string {
  const parts: string[] = [];
  if (supervisor.prefix) parts.push(supervisor.prefix);
  parts.push(supervisor.firstName);
  parts.push(supervisor.lastName);
  const name = parts.join(' ');
  return supervisor.suffix ? `${name}, ${supervisor.suffix}` : name;
}

// Demo mode data - sessions, activity, alerts
export type SessionAttendance = 'attended' | 'missed' | 'cancelled' | 'pending';

export interface SessionNotes {
  topics: string[];
  supervisorComments: string;
  goalsDiscussed: string[];
  actionItems: string[];
}

export interface MockSession {
  id: string;
  date: string;
  time: string;
  duration: string;
  superviseeIds: string[]; // Array for individual (1), dyad (2), and group (3+) sessions
  supervisorId: string;
  attendance: SessionAttendance;
  sessionType: 'individual' | 'dyad' | 'group';
  notes?: SessionNotes;
}

export interface MockActivity {
  id: string;
  date: string;
  superviseeId: string;
  description: string;
}

export interface MockLicensureProgress {
  superviseeId: string;
  hoursCompleted: number;
  hoursRequired: number;
}

export const MOCK_SESSIONS: MockSession[] = [
  {
    id: '1',
    date: 'Jan 20, 2026',
    time: '11:00am',
    duration: '1 hour',
    superviseeIds: ['101'],
    supervisorId: '1',
    attendance: 'attended',
    sessionType: 'individual',
    notes: {
      topics: ['Case conceptualization', 'CBT techniques for anxiety'],
      supervisorComments:
        'Excellent progress on applying CBT techniques. Bonnie demonstrated strong understanding of cognitive restructuring with her client. We reviewed her case notes and discussed strategies for handling resistance.',
      goalsDiscussed: [
        'Continue developing case conceptualization skills',
        'Practice motivational interviewing techniques',
      ],
      actionItems: ['Review MI workbook chapters 3-4', 'Submit case presentation by next session'],
    },
  },
  {
    id: '2',
    date: 'Jan 20, 2026',
    time: '9:30am',
    duration: '1.5 hours',
    superviseeIds: ['102', '105'],
    supervisorId: '2',
    attendance: 'attended',
    sessionType: 'dyad',
    notes: {
      topics: ['Documentation practices', 'Crisis intervention review', 'Peer consultation'],
      supervisorComments:
        'Great dyad session with Ezra and Sean. Both are improving on note structure and we had a productive peer consultation segment where they reviewed each other’s documentation. We practiced crisis assessment using recent case examples from both clinicians.',
      goalsDiscussed: [
        'Improve clinical documentation',
        'Develop crisis intervention confidence',
        'Build peer consultation skills',
      ],
      actionItems: [
        'Practice SOAP note format for next 3 sessions',
        'Complete crisis training module',
        'Schedule peer review exchange',
      ],
    },
  },
  {
    id: '3',
    date: 'Jan 18, 2026',
    time: '2:00pm',
    duration: '1 hour',
    superviseeIds: ['103'],
    supervisorId: '3',
    attendance: 'cancelled',
    sessionType: 'individual',
  },
  {
    id: '4',
    date: 'Jan 17, 2026',
    time: '10:00am',
    duration: '2 hours',
    superviseeIds: ['103', '104', '101'], // Group session with 3 supervisees
    supervisorId: '3',
    attendance: 'attended',
    sessionType: 'group',
    notes: {
      topics: ['Group dynamics', 'Ethical boundaries', 'Self-care strategies'],
      supervisorComments:
        'Productive group session. All supervisees engaged well in the ethical boundary discussion. Rachel shared a valuable case example that sparked meaningful conversation. Ended with self-care check-in.',
      goalsDiscussed: [
        'Maintain professional boundaries',
        'Prioritize self-care during high caseload periods',
      ],
      actionItems: [
        'Review ethics code sections 3.05-3.08',
        'Complete self-care assessment worksheet',
      ],
    },
  },
  {
    id: '5',
    date: 'Jan 15, 2026',
    time: '3:30pm',
    duration: '1 hour',
    superviseeIds: ['105'],
    supervisorId: '4',
    attendance: 'attended',
    sessionType: 'individual',
  },
  {
    id: '6',
    date: 'Jan 14, 2026',
    time: '11:00am',
    duration: '1 hour',
    superviseeIds: ['106'],
    supervisorId: 'motivo-1',
    attendance: 'cancelled',
    sessionType: 'individual',
  },
  {
    id: '7',
    date: 'Jan 12, 2026',
    time: '4:00pm',
    duration: '1 hour',
    superviseeIds: ['103'],
    supervisorId: '3',
    attendance: 'attended',
    sessionType: 'individual',
  },
  {
    id: '8',
    date: 'Jan 10, 2026',
    time: '1:00pm',
    duration: '2 hours',
    superviseeIds: ['103', '104'],
    supervisorId: '3',
    attendance: 'attended',
    sessionType: 'dyad',
    notes: {
      topics: ['Family systems theory', 'Countertransference', 'Case presentations'],
      supervisorComments:
        'Both Ethel and Rachel presented challenging cases involving family dynamics. Excellent discussion about countertransference and how to navigate complex family systems. The dyad format allowed them to learn from each other’s experiences.',
      goalsDiscussed: [
        'Apply family systems concepts in practice',
        'Recognize and manage countertransference',
        'Develop confidence in case conceptualization',
      ],
      actionItems: [
        'Read assigned chapter on Bowen family systems theory',
        'Journal about countertransference experiences',
        'Prepare case presentation for next month',
      ],
    },
  },
  {
    id: '9',
    date: 'Jan 8, 2026',
    time: '2:00pm',
    duration: '1 hour',
    superviseeIds: ['101'],
    supervisorId: '1',
    attendance: 'attended',
    sessionType: 'individual',
  },
  {
    id: '10',
    date: 'Jan 6, 2026',
    time: '4:00pm',
    duration: '1 hour',
    superviseeIds: ['105'],
    supervisorId: '4',
    attendance: 'attended',
    sessionType: 'individual',
  },
];

export const MOCK_ACTIVITIES: MockActivity[] = [
  {
    id: '1',
    date: 'Today at 8:15am',
    superviseeId: '101',
    description: 'reached 50% licensure!',
  },
  {
    id: '2',
    date: 'Tuesday, July 30 at 3:00pm',
    superviseeId: '103',
    description: 'missed a session with Leon Jang',
  },
  {
    id: '3',
    date: 'Monday, July 29 at 11:18am',
    superviseeId: '104',
    description: 'had their first session',
  },
];

export const MOCK_LICENSURE_PROGRESS: MockLicensureProgress[] = [
  {
    superviseeId: '106',
    hoursCompleted: 98,
    hoursRequired: 102,
  },
  {
    superviseeId: '102',
    hoursCompleted: 75,
    hoursRequired: 80,
  },
];

// Motivo supervisors (shown after upgrade)
export const MOTIVO_SUPERVISORS: MockSupervisor[] = [
  {
    id: 'motivo-1',

    firstName: 'Alicia',
    lastName: 'Maelle',
    suffix: 'LCSW',
    email: 'alicia@obscurhealth.com',
    avatarUrl: aliciaMAvatar,
    bio: 'I am a Motivo-vetted clinical supervisor dedicated to supporting associate therapists on their path to licensure. With expertise in CBT, DBT, and EMDR, I provide comprehensive supervision that balances clinical skill development with professional growth. I believe in creating a supportive environment where supervisees feel comfortable exploring challenging cases and developing their unique therapeutic approach.',
    isVetted: true,
    isMotivoSupervisor: true,
    profile: {
      phone: '212-747-8273',
      address: {
        street: '33 Lumiere Street',
        city: 'New York',
        state: 'NY',
        zip: '10938',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Esquie Monoco',
        phone: '212-746-3917',
        email: 'eskimi@gmail.com',
      },
      licenses: [
        {
          type: 'LCSW',
          state: 'California',
          number: '1233457',
          expirationDate: 'Oct 2027',
          canSuperviseFor: true,
        },
        {
          type: 'LPC',
          state: 'Oregon',
          number: '3836261',
          expirationDate: 'Mar 2028',
        },
      ],
      supervisionCredentials: [
        {
          name: 'Certified Clinical Supervisor (CCS)',
          issuingOrg: 'Center for Credentialing & Education',
          number: '4846262',
          state: 'California',
          expirationDate: 'May 2027',
        },
      ],
      telehealthDocuments: ['training_doc.pdf'],
      practicingSince: '2010',
      supervisingSince: '2018',
      resumeFile: 'aliciamaelle_resume.pdf',
      education: [
        {
          degree: 'Bachelor’s Degree',
          institution: 'Tufts University',
          year: '2008',
        },
        {
          degree: 'Master’s Degree',
          institution: 'New York University',
          year: '2014',
        },
      ],
      experience: [
        {
          title: 'Clinical Supervisor',
          organization: 'Obscur Health',
          years: '2019 - Present',
        },
      ],
    },
  },
  {
    id: 'motivo-2',

    firstName: 'Gustave',
    lastName: 'Renoir',
    suffix: 'LMFT',
    email: 'gustave@lumieretherapy.org',
    avatarUrl: gustaveRAvatar,
    bio: 'As a Licensed Marriage and Family Therapist and AAMFT Approved Supervisor, I bring over 12 years of experience in couples therapy and family systems work. My supervision philosophy centers on helping clinicians understand the relational context of their clients while developing their own therapeutic presence. I specialize in emotionally focused therapy and Gottman Method approaches.',
    isVetted: true,
    isMotivoSupervisor: true,
    profile: {
      phone: '212-555-4321',
      address: {
        street: '456 Art Avenue',
        city: 'New York',
        state: 'NY',
        zip: '10019',
      },
      timezone: 'Eastern Standard Time (EST)',
      emergencyContact: {
        name: 'Claude Monet',
        phone: '212-555-8765',
        email: 'cmonet@gmail.com',
      },
      licenses: [
        {
          type: 'LMFT',
          state: 'New York',
          number: '5149372',
          expirationDate: 'Mar 2027',
          canSuperviseFor: true,
        },
      ],
      supervisionCredentials: [
        {
          name: 'AAMFT Approved Supervisor',
          issuingOrg: 'AAMFT',
          number: '8372614',
          state: 'New York',
          expirationDate: 'Aug 2026',
        },
      ],
      telehealthDocuments: ['telehealth_training.pdf'],
      practicingSince: '2013',
      supervisingSince: '2019',
      resumeFile: 'gustave_renoir_resume.pdf',
      education: [
        {
          degree: 'Bachelor’s Degree',
          institution: 'Boston College',
          year: '2009',
        },
        {
          degree: 'Master’s Degree',
          institution: 'Columbia University',
          year: '2013',
        },
      ],
      experience: [
        {
          title: 'Lead Family Therapist',
          organization: 'Lumiere Therapy',
          years: '2018 - Present',
        },
      ],
    },
  },
];

type SupervisorMatch = { supervisorId: string; superviseeId: string };

const SUPERVISOR_MATCHES: SupervisorMatch[] = [
  { supervisorId: '1', superviseeId: '101' }, // Jennifer Gengar ↔ Bonnie Raitt
  { supervisorId: '2', superviseeId: '102' }, // Maisie Hampshere ↔ Ezra Furman
  { supervisorId: '3', superviseeId: '103' }, // Leon Jang ↔ Ethel Cain
  { supervisorId: '3', superviseeId: '104' }, // Leon Jang ↔ Rachel Chinouriri
  { supervisorId: '4', superviseeId: '105' }, // Bea Ocasio ↔ Sean Paul
  { supervisorId: 'motivo-1', superviseeId: '106' }, // Alicia Maelle ↔ Arlo Parks
  { supervisorId: 'motivo-1', superviseeId: '105' }, // Alicia Maelle ↔ Sean Paul
  { supervisorId: 'motivo-2', superviseeId: '103' }, // Gustave Renoir ↔ Ethel Cain
  { supervisorId: 'motivo-2', superviseeId: '104' }, // Gustave Renoir ↔ Rachel Chinouriri
  { supervisorId: 'motivo-2', superviseeId: '102' }, // Gustave Renoir ↔ Ezra Furman
  { supervisorId: 'motivo-2', superviseeId: '101' }, // Gustave Renoir ↔ Bonnie Raitt
];

// Demo mode stats
export const DEMO_STATS = {
  superviseeCount: 6,
  supervisorCount: 4,
  supervisionHours: 38,
  sessionAttendance: 96,
};

// Alert banners for demo mode
export const DEMO_ALERTS = [
  {
    id: '2',
    type: 'details_out_of_date' as const,
    message: '1 supervisor has an out of date license that needs to be updated',
  },
];

export function getDemoAlerts() {
  return DEMO_ALERTS;
}

export function getInHouseSupervisorsForMode(): MockSupervisor[] {
  return MOCK_SUPERVISORS;
}

export function hasAnyAlerts(supervisors: MockSupervisor[]): boolean {
  return supervisors.some((s) => s.alert !== undefined);
}

export function getSuperviseeById(id: string): MockSupervisee | undefined {
  return MOCK_SUPERVISEES.find((s) => s.id === id);
}

export function getSupervisorById(id: string): MockSupervisor | undefined {
  return MOCK_SUPERVISORS.find((s) => s.id === id) || MOTIVO_SUPERVISORS.find((s) => s.id === id);
}

export function getSuperviseeIdsForSupervisor(supervisorId: string): string[] {
  return SUPERVISOR_MATCHES.filter((m) => m.supervisorId === supervisorId).map(
    (m) => m.superviseeId,
  );
}

export function getSupervisorIdForSupervisee(superviseeId: string): string | undefined {
  return SUPERVISOR_MATCHES.find((m) => m.superviseeId === superviseeId)?.supervisorId;
}

export function getSupervisorsForSupervisee(superviseeId: string): MockSupervisor[] {
  const supervisorIds = SUPERVISOR_MATCHES.filter((m) => m.superviseeId === superviseeId).map(
    (m) => m.supervisorId,
  );
  return supervisorIds
    .map((id) => getSupervisorById(id))
    .filter((s): s is MockSupervisor => s !== undefined);
}

export function getSupervisorForSupervisee(superviseeId: string): MockSupervisor | undefined {
  const supervisorId = getSupervisorIdForSupervisee(superviseeId);
  return supervisorId ? getSupervisorById(supervisorId) : undefined;
}

export function getSupervisorByIdWithMode(
  id: string,
  isUpgraded?: boolean,
): MockSupervisor | undefined {
  const inHouseSupervisors = isUpgraded ? getInHouseSupervisorsForMode() : MOCK_SUPERVISORS;
  return inHouseSupervisors.find((s) => s.id === id) || MOTIVO_SUPERVISORS.find((s) => s.id === id);
}

export function getSuperviseesForSupervisor(supervisor: MockSupervisor): MockSupervisee[] {
  return getSuperviseeIdsForSupervisor(supervisor.id)
    .map((id) => getSuperviseeById(id))
    .filter((s): s is MockSupervisee => s !== undefined);
}

// Session utility functions
export function getSupervisorForSession(session: MockSession): MockSupervisor | undefined {
  return getSupervisorById(session.supervisorId);
}

export function getSuperviseesForSession(session: MockSession): MockSupervisee[] {
  return session.superviseeIds
    .map((id) => getSuperviseeById(id))
    .filter((s): s is MockSupervisee => s !== undefined);
}
